import React from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { invalidateMeCache } from '../lib/meClient';
import { resolvePostLoginPath, takeReturnPath } from '../lib/postLoginRedirect';
import { getSupabaseAuthUrlError, isExpiredAuthLinkError } from '../lib/supabaseAuthUrlErrors';
import type { Session } from '@supabase/supabase-js';
import { PageLoadingState } from '../components/LoadingSpinner';

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const authUrlError = getSupabaseAuthUrlError();
    if (isExpiredAuthLinkError(authUrlError)) {
      navigate('/auth/link-expired', { replace: true });
      return;
    }

    let cancelled = false;
    let handled = false;

    const handleSession = async (session: Session) => {
      if (handled || cancelled) return;
      handled = true;
      try {
        // If this user was invited (pending_instance_id in metadata), they still
        // need to set a proper password and their full name before entering the app.
        const meta = session.user.user_metadata as Record<string, unknown>;
        if (meta?.pending_instance_id) {
          if (!cancelled) navigate('/auth/complete-profile', { replace: true });
          return;
        }

        const res = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ access_token: session.access_token }),
        });
        const payload = (await res.json().catch(() => null)) as
          | {
              activeInstance?: { id?: string };
              pending_approval?: boolean;
              error?: string;
              code?: string;
            }
          | null;
        if (res.status === 202 && payload?.pending_approval) {
          if (!cancelled) navigate('/pending-approval', { replace: true });
          return;
        }
        if (payload?.code === 'trial_ended') {
          if (!cancelled) navigate('/login?reason=trial_ended', { replace: true });
          return;
        }
        if (!res.ok) throw new Error(payload?.error ?? 'Login fehlgeschlagen');
        invalidateMeCache();
        const instanceId = String(payload?.activeInstance?.id ?? '');
        const next = takeReturnPath(new URLSearchParams(window.location.search).get('next'));
        if (!cancelled) navigate(resolvePostLoginPath(instanceId, next), { replace: true });
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Login fehlgeschlagen');
      }
    };

    // Supabase auto-exchanges the PKCE code from the URL on load.
    // We listen for the resulting SIGNED_IN / RECOVERY event instead of calling
    // exchangeCodeForSession manually (which would cause a double-exchange error).
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' && session) {
        // Password reset link clicked → go to the reset form
        if (!handled && !cancelled) {
          handled = true;
          navigate('/auth/reset-password', { replace: true });
        }
      } else if (event === 'SIGNED_IN' && session) {
        void handleSession(session);
      } else if (event === 'INITIAL_SESSION' && session) {
        void handleSession(session);
      }
    });

    // Timeout fallback: if nothing happens within 12s show a generic error
    const timeout = window.setTimeout(() => {
      if (!handled && !cancelled) {
        setError('Zeitüberschreitung bei der Anmeldung. Bitte erneut versuchen.');
      }
    }, 12000);

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [navigate]);

  if (error) {
    return (
      <div className="theme-light min-h-screen bg-pipedrive-bg flex items-center justify-center p-6 text-slate-900">
        <div className="w-full max-w-md rounded-xl border border-pipedrive-border bg-white p-6 shadow-sm text-center">
          <p className="text-sm text-red-600 mb-4">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="text-sm text-pipedrive-blue hover:underline"
          >
            Zurück zum Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="theme-light min-h-screen flex items-center justify-center bg-pipedrive-bg text-slate-900">
      <PageLoadingState />
    </div>
  );
}
